/**
 * document-items-draft.ts — logika stanu edytowalnego grida pozycji (increment 2).
 *
 * Mapowania serwer→draft→payload + KLIENTOWY podgląd przeliczeń. Podgląd jest
 * przybliżony (`Math.round` do 2 miejsc) i służy WYŁĄCZNIE do orientacji podczas
 * wpisywania — źródłem prawdy jest silnik serwerowy (`apps/api/lib/documents/
 * items-calc.ts`, BigInt, half-away-from-zero) uruchamiany przy zapisie. NIE
 * dublujemy silnika (decyzja planu: „prosty preview wystarczy, oznacz jako preview").
 */

import type { ProductPickResult } from '@/components/ui/ProductSearchPicker';
import type { DictionaryItem } from '@/hooks/useDictionary';

import type {
  DocumentItemPayload,
  DocumentLineItem,
  DocumentLineItemDraft,
} from './document-items';
import { parseVatRateCode } from './vat-rate';

/** Nowy pusty wiersz (ilość domyślnie „1" — najczęstszy przypadek). */
export function emptyDraft(): DocumentLineItemDraft {
  return {
    key: crypto.randomUUID(),
    productId: null,
    productCode: '',
    name: '',
    unit: '',
    quantity: '1',
    unitPriceNet: '',
    discountPercent: '',
    vatRateCode: '',
    vatRate: '',
    pkwiu: '',
    cn: '',
    gtin: '',
    extValues: {},
  };
}

/**
 * Pozycja z odpowiedzi GET → draft edytowalny. Cena bierze się z wariantu
 * WALUTOWEGO (`unitPriceNetInCurrency`) — to jest wartość w walucie dokumentu,
 * którą użytkownik edytuje; bazowy (PLN) odpowiednik wyprowadza serwer.
 */
export function serverItemToDraft(item: DocumentLineItem): DocumentLineItemDraft {
  return {
    key: item.id,
    productId: item.productId,
    productCode: item.productCode ?? '',
    name: item.name,
    unit: item.unit ?? '',
    quantity: item.quantity ?? '',
    unitPriceNet: item.unitPriceNetInCurrency ?? item.unitPriceNet ?? '',
    discountPercent: item.discountPercent ?? '',
    vatRateCode: item.vatRateCode ?? '',
    vatRate: item.vatRate ?? '',
    pkwiu: item.pkwiu ?? '',
    cn: item.cn ?? '',
    gtin: item.gtin ?? '',
    extValues: item.extValues ?? {},
  };
}

/**
 * Migawka pola z kartoteki towaru na draft wiersza — WYDZIELONE z
 * `DocumentItemsEditor.applyProduct` (F2 „advanced product-picker modal + multi-add
 * wiring"), żeby ta sama logika mapowania obsłużyła zarówno pojedynczy wybór
 * (`ProductSearchPicker`, zamienia produkt NA MIEJSCU istniejącego wiersza) jak i
 * wielokrotne dodanie z zaawansowanego pickera (`ProductPickerModal` →
 * `DocumentItemsEditor.handlePickerConfirm`, po jednym nowym wierszu na towar).
 * Czysta funkcja — bez efektów, bez zależności od Reacta. `base` niesie pola NIE
 * dotykane przez wybór towaru (np. `key`, `quantity`, `discountPercent`, `gtin`).
 */
export function draftFromProduct(
  base: DocumentLineItemDraft,
  product: ProductPickResult,
  vatAllItems: DictionaryItem[],
): DocumentLineItemDraft {
  // Stawka towaru („23%") → kod słownika („23") → wartość liczbowa z pozycji słownika
  // (pełna lista — kod towaru/wczytanej pozycji może wskazywać kod, który w
  // międzyczasie przestał być aktywny/selectable w słowniku, a mimo to ma poprawną
  // wartość liczbową do przeliczeń).
  const vatCode = parseVatRateCode(product.vatRate);
  const vatMatch = vatAllItems.find((i) => i.code === vatCode);
  return {
    ...base,
    productId: product.id,
    productCode: product.code ?? '',
    name: product.name,
    unit: product.unit ?? '',
    unitPriceNet: product.basePrice ?? '',
    pkwiu: product.pkwiu ?? '',
    cn: product.cn ?? '',
    // GTIN: kartoteka `products` nie ma tej kolumny — zostaje z `base`, ręcznie edytowalne.
    vatRateCode: vatMatch ? vatMatch.code : vatCode,
    // `value` pozycji słownika to string|null — pozycja bez wartości liczbowej
    // (np. „zw/np" spoza standardowego seedu) mapuje się na '0' (stawka zwolniona
    // = 0% dla przeliczeń) zamiast na pusty string, który blokowałby zapis
    // w `validateItemsDraft` (wymaga niepustego `vatRate`).
    vatRate: vatMatch?.value ?? '0',
  };
}

/** Draft → kształt wysyłany do API. Rabat mapowany na tryb `percent` (MVP). */
export function draftToPayload(d: DocumentLineItemDraft): DocumentItemPayload {
  const pct = d.discountPercent.trim();
  const pctNum = Number(pct);
  const hasDiscount = pct !== '' && Number.isFinite(pctNum) && pctNum !== 0;
  return {
    productId: d.productId,
    productCode: d.productCode.trim() || null,
    name: d.name.trim(),
    unit: d.unit.trim() || null,
    quantity: d.quantity.trim(),
    unitPriceNet: d.unitPriceNet.trim(),
    ...(hasDiscount ? { discount: { mode: 'percent' as const, value: pct } } : {}),
    vatRateCode: d.vatRateCode.trim(),
    vatRate: d.vatRate.trim(),
    pkwiu: d.pkwiu.trim() || null,
    cn: d.cn.trim() || null,
    gtin: d.gtin.trim() || null,
    // ZAWSZE spread — replace-set: brak tego pola w payloadzie wyczyściłby
    // zapisane wartości ext (kontrakt trwałości, patrz `DocumentLineItemDraft.extValues`).
    extValues: d.extValues,
  };
}

export interface LinePreview {
  valueNet: number;
  valueVat: number;
  valueGross: number;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Klientowy PODGLĄD kwot wiersza (przybliżony — patrz nagłówek pliku). */
export function computeLinePreview(d: DocumentLineItemDraft): LinePreview {
  const qty = Number(d.quantity) || 0;
  const price = Number(d.unitPriceNet) || 0;
  const pct = Number(d.discountPercent) || 0;
  const rate = Number(d.vatRate) || 0;
  const net = round2(qty * price * (1 - pct / 100));
  const vat = round2(net * (rate / 100));
  return { valueNet: net, valueVat: vat, valueGross: round2(net + vat) };
}

/** Klientowy PODGLĄD sum nagłówka (suma podglądów wierszy). */
export function computeTotalsPreview(drafts: DocumentLineItemDraft[]): LinePreview {
  return drafts.reduce<LinePreview>(
    (acc, d) => {
      const p = computeLinePreview(d);
      return {
        valueNet: round2(acc.valueNet + p.valueNet),
        valueVat: round2(acc.valueVat + p.valueVat),
        valueGross: round2(acc.valueGross + p.valueGross),
      };
    },
    { valueNet: 0, valueVat: 0, valueGross: 0 },
  );
}

/**
 * Zamienia polski zapis dziesiętny (przecinek) na kropkę oczekiwaną przez backend
 * (`^-?\d+(\.\d{1,6})?$`). WYŁĄCZNIE zamiana separatora — NIE waliduje formatu
 * (np. `'1,234,56'` da `'1.234.56'`, wciąż niepoprawne dla backendu). Stosowane
 * na wejściu do pól liczbowych grida (ilość/cena/rabat), żeby draft trzymał zawsze
 * zapis kropkowy. Strażnikiem formatu jest `validateItemsDraft` poniżej.
 */
export function normalizeDecimalInput(raw: string): string {
  return raw.replace(/,/g, '.').trim();
}

/**
 * Klucze wierszy NIEPRAWIDŁOWYCH — klient blokuje zapis, gdy zbiór niepusty (patrz
 * strony detalu dokumentów), zamiast wysyłać zły wiersz i pokazywać dopiero 400.
 * Wiersz jest zły gdy:
 *  - brakuje któregoś pola wymaganego przez backend (`name`/`quantity`/`unitPriceNet`/
 *    `vatRateCode`/`vatRate` — te same, których brak backend odrzuca 400-tką), LUB
 *  - `quantity` lub `unitPriceNet` jest niepuste, ale nie parsuje się do skończonej
 *    liczby (np. `'1.234.56'`, `'abc'`) — backend wymaga formatu `^-?\d+(\.\d{1,6})?$`,
 *    więc źle sformatowaną wartość łapiemy tu zamiast pozwolić na ciche 0 w podglądzie
 *    i 400 z backendu przy zapisie, LUB
 *  - rabat jest spoza zakresu 0–100% (decyzja biznesowa: rabat faktury nie bywa ujemny
 *    ani >100%; backend waliduje tylko format liczby, więc zakres pilnujemy tu). Pusty
 *    rabat = brak rabatu (0%) = poprawny.
 */
export function validateItemsDraft(rows: DocumentLineItemDraft[]): Set<string> {
  const invalid = new Set<string>();
  for (const row of rows) {
    const discount = row.discountPercent.trim();
    const discountNum = Number(discount);
    const discountOutOfRange =
      discount !== '' && (Number.isNaN(discountNum) || discountNum < 0 || discountNum > 100);
    const quantity = row.quantity.trim();
    const quantityNotFinite = quantity !== '' && !Number.isFinite(Number(quantity));
    const unitPriceNet = row.unitPriceNet.trim();
    const unitPriceNetNotFinite = unitPriceNet !== '' && !Number.isFinite(Number(unitPriceNet));
    if (
      row.name.trim() === '' ||
      quantity === '' ||
      unitPriceNet === '' ||
      row.vatRateCode.trim() === '' ||
      row.vatRate.trim() === '' ||
      quantityNotFinite ||
      unitPriceNetNotFinite ||
      discountOutOfRange
    ) {
      invalid.add(row.key);
    }
  }
  return invalid;
}
